import type { AnimateDigitProps } from '@c/animate-digit'
import type { TooltipProps } from 'antd/lib/tooltip'
import type { PropsWithChildren, ReactNode } from 'react'
import AnimateDigit from '@c/animate-digit'
import LoadingBox from '@c/loading/loading-box'
import { isBoolean, isObject } from '@jhqn/utils'
import { Tooltip } from 'antd'
import cls from './index.module.scss'

interface ColProps extends Omit<TooltipProps, 'title'>, PropsWithChildren {
  /** 左侧标签 */
  label?: ReactNode
  /**
   * 右侧值
   * @description 优先级低于 children
   */
  content?: ReactNode
  /** 加载中状态 */
  loading?: boolean
  /** 指定气泡显示内容 */
  tipContent?: string | number | null
  /**
   * 延迟时间，单位秒
   * @description 适用于内容加载不及时，气泡显示时机不准确的情况，例如：内容为数字动画，延迟时间=数字动画延迟时间+数字动画持续时间
   */
  delay?: number
  /**
   * 溢出时是否显示文字提示
   * @default true
   */
  tooltip?: boolean
  /** 总是显示文字提示 */
  alwaysTip?: boolean
  /**
   * 是否显示标签后的一个冒号
   * @default true
   */
  colon?: boolean
  /**
   * 将块容器的内容限制在指定的行数内
   *  @default 1
   */
  line?: number
  /**
   * 空值状态占位内容
   * @default -
   */
  emptyText?: React.ReactNode
  /** 数字动画配置 */
  animate?: true | Omit<AnimateDigitProps, 'value'>
  labelClassName?: string
  contentClassName?: string
}

const TooltipCol = memo(
  ({
    label,
    content,
    loading,
    tipContent,
    delay,
    tooltip = true,
    alwaysTip = false,
    colon = true,
    line = 1,
    emptyText = '-',
    animate,
    className,
    labelClassName,
    contentClassName,
    style,
    children,
    ...tooltipProps
  }: ColProps) => {
    const [isTruncated, setIsTruncated] = useState(false)
    const elementRef = useRef<HTMLDivElement>(null)

    const useContent = useMemo(
      () => (isDef(children) ? children : isDef(content) ? content : emptyText),
      [content, children]
    )

    const useDelay = useMemo(() => {
      let num = 0
      if (isDef(delay)) num = delay
      else if (isBoolean(animate)) num = 0.2 + 0.5
      else if (isObject(animate)) num = (animate.delay || 0.2) + (animate.duration || 0.5)
      return num
    }, [delay])

    useEffect(() => {
      const timeout = setTimeout(() => {
        const element = elementRef.current
        if (element) {
          const { scrollHeight, clientHeight, scrollWidth, clientWidth } = element
          setIsTruncated(scrollHeight > clientHeight || scrollWidth > clientWidth)
        }
      }, useDelay * 1000)
      return () => clearTimeout(timeout)
    }, [useContent])

    const renderedContent = useMemo(() => {
      // @ts-expect-error isNumeric
      if (!!animate && isNumeric(useContent)) {
        const num = Number(useContent)
        if (isBoolean(animate)) return <AnimateDigit value={num} />
        return <AnimateDigit {...animate} value={num} />
      }
      return useContent
    }, [useContent])

    const useTipContent = useMemo(() => {
      if ((isTruncated && tooltip) || alwaysTip) {
        if (isDef(tipContent)) return tipContent
        return useContent
      }
    }, [isTruncated, tooltip, alwaysTip, useContent])

    return (
      <div className={clsx('flex', cls.tooltip_col, className)} style={style}>
        {label && (
          <div className={clsx('whitespace-nowrap', labelClassName)}>
            {label}
            {colon && '：'}
          </div>
        )}
        <LoadingBox loading={loading} type="wrap">
          <Tooltip className={cls.tooltip_col_overlay} placement="top" title={useTipContent} {...tooltipProps}>
            <div
              ref={elementRef}
              className={clsx(cls.inner_html_truncate, contentClassName)}
              style={{ '--tooltip-col-lines': line } as React.CSSProperties}
            >
              {renderedContent}
            </div>
          </Tooltip>
        </LoadingBox>
      </div>
    )
  }
)
export default TooltipCol
